import React, { useState, useContext, useEffect } from 'react'
import { FirebaseContext } from '../../Firebase'
import { FormattedMessage, useIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faSave, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons'
import { faArrowToBottom } from '@fortawesome/pro-solid-svg-icons'
import { Dropdown as SemanticDropdown } from 'semantic-ui-react'
import { Dropdown } from 'react-bootstrap'
import {
  WingsModal,
  WingsEyebrow,
  WingsHeading,
  WingsText,
  WingsButton,
  WingsDetailText,
  WingsBadge,
  WingsIcon,
  WingsSearchBar,
  WingsTextArea,
  WingsDateInput,
  WingsSelectInput,
  WingsCheckbox,
} from '@wingspans-ui'
import { v4 as uuidv4 } from 'uuid'
import * as XLSX from 'xlsx'
import { CareerPlanColors } from '../../career-plans/careerPlanUtils'
import useSampleGoal from './useSampleGoal'
import './GoalTrackingTab.css'

/* ────────────────────────────────────────────────────────────────────────────
 * Constants
 * ──────────────────────────────────────────────────────────────────────────── */

const PRESET_CATEGORIES = [
  'Work',
  'Personal',
  'Health',
  'Learning',
  'School',
  'Career',
  'Finance',
  'Home',
  'Relationships',
  'Creative/Hobbies',
  'Mindfulness',
  "Something I'm Looking Forward To",
]

const SAMPLE_GOAL_CARD_ID_PREFIX = 'sample-goal::'

// Goals saved before colors existed have no `color`, so they fall back to pink.
const DEFAULT_GOAL_COLOR = 'PINK'

const colorOptionContent = (color, label) => (
  <div className="goal-tracking-color-option w-flex w-flex-row w-items-center w-justify-start">
    <span className="goal-tracking-color-indicator" style={{ backgroundColor: color }} />
    <WingsDetailText>{label}</WingsDetailText>
  </div>
)

const goalColorDropdownOptions = Object.entries(CareerPlanColors).map(([key, { label, color }]) => ({
  key,
  value: key,
  content: colorOptionContent(color, label),
  text: colorOptionContent(color, label),
}))

/* ────────────────────────────────────────────────────────────────────────────
 * Helpers
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Converts a locale date string (e.g. "5/28/2024") to the ISO date string
 * required by <input type="date"> (e.g. "2024-05-28").
 */
const toInputDate = (localeDateStr) => {
  if (!localeDateStr) return ''
  const d = new Date(localeDateStr)
  if (isNaN(d.getTime())) return ''
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, '0')
  const dd = String(d.getDate()).padStart(2, '0')
  return `${yyyy}-${mm}-${dd}`
}

/**
 * Normalizes task payloads from Firebase into consistent objects.
 * Tasks may be stored as plain strings (legacy) or partial objects.
 *
 * @returns {Array<{id: string, name: string, completed: boolean}>}
 */
const normalizeGoalTasks = (tasks = []) =>
  tasks.filter(Boolean).map((task, index) => {
    if (typeof task === 'string') {
      return { id: `task-${index + 1}`, name: task, completed: false }
    }
    return {
      id: task.id || `task-${index + 1}`,
      name: task.name || '',
      completed: Boolean(task.completed),
    }
  })

const getSampleGoalCardId = (goalId = '') => `${SAMPLE_GOAL_CARD_ID_PREFIX}${goalId}`

const isSampleGoalItem = (item = {}) =>
  Boolean(item?.isSampleGoal || item?.origin === 'sample-goal-seed' || item?.id?.startsWith(SAMPLE_GOAL_CARD_ID_PREFIX))

/** Resolves a stored color key to its CSS color, defaulting to pink. */
const getGoalColorValue = (colorKey) =>
  (CareerPlanColors[colorKey] ?? CareerPlanColors[DEFAULT_GOAL_COLOR]).color

/** Immutable Set update: returns a copy with `value` added or removed. */
const toggleSetMember = (set, value, shouldInclude) => {
  const next = new Set(set)
  shouldInclude ? next.add(value) : next.delete(value)
  return next
}

/**
 * Converts the modal's step rows into task objects, preserving the id and
 * completion state of any existing task with the same name so editing a goal
 * doesn't reset its progress.
 */
const buildTasksFromModal = (steps = [], existingTasks = []) => {
  const existingByName = normalizeGoalTasks(existingTasks).reduce((acc, task) => {
    const key = task.name.trim().toLowerCase()
    if (!acc[key]) acc[key] = []
    acc[key].push(task)
    return acc
  }, {})

  return steps
    .map((step) => step.name.trim())
    .filter(Boolean)
    .map((stepName) => {
      const existingTask = existingByName[stepName.toLowerCase()]?.shift()
      return {
        id: existingTask?.id || uuidv4(),
        name: stepName,
        completed: existingTask?.completed ?? false,
      }
    })
}

/** Converts stored tasks into the modal's editable step rows. */
const getModalStepsFromTasks = (tasks = []) =>
  normalizeGoalTasks(tasks).map((task) => ({ id: task.id, name: task.name }))

/* ────────────────────────────────────────────────────────────────────────────
 * GoalCard
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Individual goal card: category badge, name, target date, editable task
 * steps, an add-step action, and a progress bar. Sample goals are read-only
 * apart from step/goal completion.
 */
const GoalCard = ({
  item,
  tasks,
  isTasksClosed,
  isSampleGoal = false,
  isBusy = false,
  onToggleTaskVisibility,
  onToggleTask,
  onEditTask,
  onDeleteTask,
  onAddTask,
  onToggleComplete,
  onEdit,
  onDelete,
}) => {
  const intl = useIntl()
  const [editingTaskId, setEditingTaskId] = useState(null)
  const [editingTaskValue, setEditingTaskValue] = useState('')

  const { completed, name, targetDate, category, color } = item
  const hasTasks = tasks.length > 0
  const completedTaskCount = tasks.filter((t) => t.completed).length
  const progressPercent = completed ? 100 : hasTasks ? Math.round((completedTaskCount / tasks.length) * 100) : 0

  const resolvedTargetDate = targetDate
    ? new Intl.DateTimeFormat(intl.locale || 'en-US', { month: 'long', day: 'numeric', year: 'numeric' }).format(new Date(targetDate))
    : null

  /* ── Inline task editing ── */

  const startEditingTask = (task) => {
    setEditingTaskId(task.id)
    setEditingTaskValue(task.name)
  }

  const resetEditingTask = () => {
    setEditingTaskId(null)
    setEditingTaskValue('')
  }

  const saveEditingTask = (task) => {
    const trimmedValue = editingTaskValue.trim()
    if (trimmedValue) {
      onEditTask(item, task.id, trimmedValue)
    } else if (!task.name) {
      // Blank new task abandoned without a name — clean it up.
      onDeleteTask(item, task.id)
    }
    resetEditingTask()
  }

  return (
    <div className={`goal-card${completed ? ' goal-card-completed' : ''}`}>
      {/* Header: category, name, and (for real goals) the ••• menu */}
      <div className="goal-card-header w-flex w-items-start w-justify-between">
        <div className="w-pr-1">
          {category && (
            <WingsBadge
              variant="tal-e"
              size="narrow"
              className="goal-card-category w-mb-1"
              backgroundColor={getGoalColorValue(color)}
            >
              {category}
            </WingsBadge>
          )}

          <WingsHeading level={5} className={`goal-card-name${completed ? ' goal-card-name-done' : ''}`}>
            {name}
          </WingsHeading>
        </div>

        <Dropdown className="goal-card-menu" align="end">
          <Dropdown.Toggle id={`goal-menu-${item.id}`} className="goal-card-menu-toggle">
            <WingsIcon icon="ellipsis-v" color="var(--w-color-teal-900)" size="mini" />
          </Dropdown.Toggle>
          <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
            {!isSampleGoal && (
              <Dropdown.Item onClick={() => onEdit(item)}>
                <FormattedMessage id="dashboard-item-menu-edit" defaultMessage="Edit" />
              </Dropdown.Item>
            )}
            <Dropdown.Item onClick={() => onToggleComplete(item)}>
              {completed ? (
                <FormattedMessage id="goal-tracking-mark-incomplete" defaultMessage="Mark as Incomplete" />
              ) : (
                <FormattedMessage id="goal-tracking-mark-complete" defaultMessage="Mark as Complete" />
              )}
            </Dropdown.Item>
            {!isSampleGoal && (
              <Dropdown.Item
                onClick={() => onDelete(item)}
              >
                <FormattedMessage id="dashboard-item-menu-delete" defaultMessage="Delete" />
              </Dropdown.Item>
            )}
          </Dropdown.Menu>
        </Dropdown>
      </div>

      <div className="goal-card-content w-flex w-flex-column w-pb-1 w-px-1">
        {resolvedTargetDate && (
          <WingsDetailText className="goal-card-target-date">
            <b>
              <FormattedMessage id="goal-tracking-task-target-date" defaultMessage="Target Date:" />
            </b>{' '}
            {resolvedTargetDate}
          </WingsDetailText>
        )}

        {/* Task steps */}
        {!isTasksClosed && (
          <ul className="goal-card-step-list w-mt-1 w-mb-0">
            {tasks.map((task) => (
              <li className="goal-card-step-item" key={task.id}>
                <div className="goal-card-step-row">
                  <div className="goal-card-step-checkbox-wrapper">
                    <WingsCheckbox color="purple" checked={task.completed} onChange={() => onToggleTask(item, task.id)} />
                  </div>

                  {!isSampleGoal && (editingTaskId === task.id || !task.name) ? (
                    /* Edit mode — also used for freshly added (unnamed) tasks */
                    <div className="goal-card-step-edit-wrapper">
                      <WingsTextArea
                        value={editingTaskId === task.id ? editingTaskValue : ''}
                        onChange={(e) => {
                          setEditingTaskId(task.id)
                          setEditingTaskValue(e.target.value)
                        }}
                        onBlur={(e) => {
                          // Let the save/delete buttons handle their own click
                          // instead of saving on blur first.
                          const action = e.relatedTarget?.dataset?.stepAction
                          if (action === 'save' || action === 'delete') return
                          saveEditingTask(task)
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault()
                            saveEditingTask(task)
                          }
                          if (e.key === 'Escape') resetEditingTask()
                        }}
                        rows={1}
                        fullWidth
                        autoResize
                        autoFocus
                      />
                      <button
                        className="goal-card-step-action-button goal-card-step-action-cancel"
                        type="button"
                        data-step-action="delete"
                        onClick={() => {
                          const shouldDeleteTask = window.confirm(
                            intl.formatMessage({
                              id: 'goal-tracking-task-delete-warning',
                              defaultMessage: 'Are you sure you want to delete this step?',
                            })
                          )
                          if (!shouldDeleteTask) return
                          onDeleteTask(item, task.id)
                          resetEditingTask()
                        }}
                        aria-label={intl.formatMessage({ id: 'goal-tracking-task-delete', defaultMessage: 'Delete step' })}
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                      <button
                        className="goal-card-step-action-button goal-card-step-action-save"
                        type="button"
                        data-step-action="save"
                        onClick={() => saveEditingTask(task)}
                        aria-label={intl.formatMessage({ id: 'goal-tracking-task-save', defaultMessage: 'Save step' })}
                      >
                        <FontAwesomeIcon icon={faSave} />
                      </button>
                    </div>
                  ) : (
                    /* Display mode — click text to toggle, pencil to edit */
                    <>
                      <span
                        className={`goal-card-step-text${task.completed ? ' goal-card-step-text-complete' : ''}`}
                        role="button"
                        tabIndex={0}
                        onClick={() => onToggleTask(item, task.id)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault()
                            onToggleTask(item, task.id)
                          }
                        }}
                      >
                        <WingsDetailText as="span">
                          {task.name || intl.formatMessage({ id: 'goal-tracking-task-empty-step', defaultMessage: 'Click to edit' })}
                        </WingsDetailText>
                      </span>
                      {!isSampleGoal && (
                        <button
                          className="goal-card-step-action-button goal-card-step-edit-trigger"
                          type="button"
                          onClick={() => startEditingTask(task)}
                          aria-label={intl.formatMessage({ id: 'dashboard-item-menu-edit', defaultMessage: 'Edit' })}
                        >
                          <FontAwesomeIcon icon={faEdit} />
                        </button>
                      )}
                    </>
                  )}
                </div>
              </li>
            ))}

          </ul>
        )}

        {/* Footer: show/hide steps, add-step action, progress bar */}
        <div className="goal-card-footer">
          <div className="goal-card-actions w-flex w-justify-between w-items-center w-mt-1">
            {hasTasks && (
              <WingsButton
                variant="off-site"
                size="mini"
                inline
                iconName={isTasksClosed ? 'chevron-down' : 'chevron-up'}
                iconPosition="left"
                className="goal-card-action-button"
                onClick={() => onToggleTaskVisibility(item.id)}
              >
                <FormattedMessage
                  id={isTasksClosed ? 'goal-tracking-task-open-steps' : 'goal-tracking-task-close-steps'}
                  defaultMessage={isTasksClosed ? 'See Steps' : 'Close Steps'}
                />
              </WingsButton>
            )}

            {!isSampleGoal && (
              <div className="goal-card-complete-control">
                <WingsButton
                  variant="off-site"
                  size="mini"
                  inline
                  iconName="plus"
                  className="goal-card-action-button"
                  onClick={() => onAddTask(item)}
                  disabled={isBusy}
                >
                  <FormattedMessage id="goal-tracking-task-step" defaultMessage="Step" />
                </WingsButton>
              </div>
            )}
          </div>

          <div className="goal-card-progress goal-card-progress-bottom">
            <div className="goal-card-progress-bar">
              <div className="goal-card-progress-fill" style={{ width: `${progressPercent}%` }} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

/* ────────────────────────────────────────────────────────────────────────────
 * GoalTrackingTab
 * ──────────────────────────────────────────────────────────────────────────── */

/**
 * Goal Tracking dashboard tab.
 *
 * Displays a card-based grid of goals. Users filter by status (Active /
 * Completed / All), category, and search, and can add, edit, complete,
 * delete, and export goals.
 *
 * Data is stored in Firebase under `profile.goals`. The sample goal (from
 * useSampleGoal) is displayed alongside real goals but persists through its
 * own hook.
 */
const GoalTrackingTab = () => {
  const { firebase, profile, notifier } = useContext(FirebaseContext)
  const intl = useIntl()
  const {
    goal: sampleGoal,
    loading: sampleGoalLoading,
    saving: sampleGoalSaving,
    toggleStepComplete: toggleSampleGoalStepComplete,
    setComplete: setSampleGoalComplete,
  } = useSampleGoal()

  /* ── State ── */

  // List view
  const [filter, setFilter] = useState('active')
  const [categoryFilter, setCategoryFilter] = useState('all')
  const [itemSearchQuery, setItemSearchQuery] = useState('')
  const [items, setItems] = useState([])
  const [justCompleted, setJustCompleted] = useState(new Set())
  const [temporaryTasksByGoalId, setTemporaryTasksByGoalId] = useState({})
  const [closedTaskLists, setClosedTaskLists] = useState(new Set())
  const [addedGoalCategories, setAddedGoalCategories] = useState([])

  // Add/Edit modal
  const [showModal, setShowModal] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [modalValue, setModalValue] = useState('')
  const [modalTargetDate, setModalTargetDate] = useState('')
  const [modalCategory, setModalCategory] = useState('')
  const [modalColor, setModalColor] = useState(DEFAULT_GOAL_COLOR)
  const [modalLinked, setModalLinked] = useState(true)
  const [modalSteps, setModalSteps] = useState([])
  const [focusedStepId, setFocusedStepId] = useState(null)
  const [categorySearchQuery, setCategorySearchQuery] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [pendingDeleteItem, setPendingDeleteItem] = useState(null)

  /* ── Derived data ── */

  // Stable card id for the sample goal — used in effects so they don't need
  // to depend on the (re-created every render) sample goal item object.
  const sampleGoalCardId = sampleGoal ? getSampleGoalCardId(sampleGoal.id) : null

  // Present the sample goal in the same shape as real goal items.
  const sampleGoalItem =
    sampleGoal
      ? {
          id: sampleGoalCardId,
          name: sampleGoal.title,
          description: sampleGoal.description,
          category: intl.formatMessage({ id: 'goal-tracking-sample-goal-category', defaultMessage: 'Sample Goal' }),
          origin: sampleGoal.origin,
          completed: sampleGoal.complete,
          isSampleGoal: true,
          tasks: sampleGoal.steps.map((step) => ({
            id: step.key,
            name: step.label,
            description: step.description,
            completed: step.complete,
          })),
        }
      : null

  const displayItems = sampleGoalItem ? [...items, sampleGoalItem] : items

  // Status-tab filtering. Items completed this session stay in Active (via
  // justCompleted) so the progress bar can animate before they move to the
  // Completed tab on the next data reload.
  const sectionItems = (() => {
    if (filter === 'active') return displayItems.filter((item) => !item.completed || justCompleted.has(item.id))
    if (filter === 'done') return displayItems.filter((item) => item.completed && !justCompleted.has(item.id))
    return [...displayItems]
  })()

  // Category options for the toolbar filter — only categories present in the
  // current status tab, plus "All Categories".
  const categoryFilterOptions = (() => {
    const categoriesByKey = sectionItems.reduce((acc, item) => {
      const category = item?.category?.trim()
      if (!category) return acc
      const key = category.toLowerCase()
      if (!acc.has(key)) acc.set(key, category)
      return acc
    }, new Map())

    const sortedCategories = Array.from(categoriesByKey.values()).sort((a, b) => a.localeCompare(b))

    return [
      {
        key: 'all',
        text: intl.formatMessage({ id: 'goal-tracking-filter-all-categories', defaultMessage: 'All Categories' }),
        value: 'all',
      },
      ...sortedCategories.map((category) => ({
        key: category.toLowerCase(),
        text: category,
        value: category.toLowerCase(),
      })),
    ]
  })()

  // Category options for the modal dropdown — presets, categories already in
  // use, and any custom ones added this session, deduped case-insensitively.
  const goalCategoryDropdownOptions = (() => {
    const existingGoals = Array.isArray(profile?.goals) ? profile.goals : []
    const inUse = [...existingGoals, ...items].map((goal) => goal?.category).filter(Boolean)

    const uniqueCategories = Array.from(
      [...PRESET_CATEGORIES, ...inUse, ...addedGoalCategories]
        .reduce((acc, category) => {
          const trimmed = (category ?? '').trim()
          if (!trimmed) return acc
          const key = trimmed.toLowerCase()
          if (!acc.has(key)) acc.set(key, trimmed)
          return acc
        }, new Map())
        .values()
    ).sort((a, b) => a.localeCompare(b))

    return uniqueCategories.map((category) => ({ key: category, text: category, value: category }))
  })()

  // Final card list: category filter + search + newest-first sort.
  const filteredItems = (() => {
    let result = [...sectionItems]

    if (categoryFilter !== 'all') {
      result = result.filter((item) => item?.category?.trim()?.toLowerCase() === categoryFilter)
    }

    const query = itemSearchQuery.trim().toLowerCase()
    if (query) {
      result = result.filter((item) => {
        if ((item?.name ?? '').toLowerCase().includes(query)) return true
        if ((item?.category ?? '').toLowerCase().includes(query)) return true
        return normalizeGoalTasks(item?.tasks).some((task) => task.name.toLowerCase().includes(query))
      })
    }

    return result.sort((a, b) => {
      const dateA = a.date ? new Date(a.date).getTime() : 0
      const dateB = b.date ? new Date(b.date).getTime() : 0
      return dateB - dateA
    })
  })()

  /* ── Data loading & sync ── */

  // Load goals from the profile whenever it changes, and reset per-view state
  // (filters, search, session-completion tracking).
  useEffect(() => {
    if (!profile) return

    let nextItems = []
    const raw = profile.goals
    if (Array.isArray(raw)) {
      nextItems = raw
    } else if (typeof raw === 'string' && raw) {
      // Legacy: a single string value becomes a one-item list.
      nextItems = [{ id: uuidv4(), name: raw, completed: false, date: new Date().toLocaleDateString() }]
    }

    setItems(nextItems)

    // Drop closed-steps entries for cards that no longer exist.
    const visibleItemIds = new Set(nextItems.map((item) => item?.id).filter(Boolean))
    if (sampleGoalCardId) visibleItemIds.add(sampleGoalCardId)
    setClosedTaskLists((prev) => new Set(Array.from(prev).filter((itemId) => visibleItemIds.has(itemId))))

    setJustCompleted(new Set())
    setTemporaryTasksByGoalId({})
    setCategoryFilter('all')
    setItemSearchQuery('')
  }, [profile, sampleGoalCardId])

  // If the selected category no longer exists in the current tab (e.g. after
  // switching status filters), fall back to "All Categories".
  useEffect(() => {
    if (categoryFilter === 'all') return
    const stillExists = categoryFilterOptions.some((option) => option.value === categoryFilter)
    if (!stillExists) setCategoryFilter('all')
  }, [categoryFilter, categoryFilterOptions])

  /* ── Item handlers ── */

  const saveItems = (updatedItems) => firebase.updateProfile({ data: { goals: updatedItems } })

  /** Tasks for a card: saved tasks first, falling back to unsaved local edits. */
  const getGoalTasks = (item) => {
    if (!item) return []
    const savedTasks = normalizeGoalTasks(item.tasks)
    if (savedTasks.length > 0) return savedTasks
    return normalizeGoalTasks(temporaryTasksByGoalId[item.id])
  }

  const handleToggleComplete = async (item) => {
    const nowCompleted = !item.completed

    if (isSampleGoalItem(item)) {
      // Match standard-goal UX: show completion progress in Active briefly,
      // then move the sample goal to Completed after the transition.
      setJustCompleted((prev) => toggleSetMember(prev, item.id, nowCompleted))

      const success = await setSampleGoalComplete(nowCompleted)
      if (!success) {
        setJustCompleted((prev) => toggleSetMember(prev, item.id, !nowCompleted))
        return
      }

      if (nowCompleted) {
        window.setTimeout(() => {
          setJustCompleted((prev) => toggleSetMember(prev, item.id, false))
        }, 1000)
      }
      return
    }

    // Track optimistically so the card stays in the Active tab this session.
    setJustCompleted((prev) => toggleSetMember(prev, item.id, nowCompleted))

    setItems((prev) => {
      const updated = prev.map((i) => (i.id === item.id ? { ...i, completed: nowCompleted } : i))
      saveItems(updated)
      return updated
    })
  }

  const handleDelete = (itemId) => {
    const targetItem = displayItems.find((item) => item.id === itemId)
    if (isSampleGoalItem(targetItem)) return

    setItems((prev) => {
      const updated = prev.filter((i) => i.id !== itemId)
      saveItems(updated)
      return updated
    })

    // Clean up all per-card state.
    setJustCompleted((prev) => toggleSetMember(prev, itemId, false))
    setClosedTaskLists((prev) => toggleSetMember(prev, itemId, false))
    setTemporaryTasksByGoalId((prev) => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })
  }

  const handleOpenDeleteModal = (item) => {
    if (!item || isSampleGoalItem(item)) return
    setPendingDeleteItem(item)
  }

  const handleCloseDeleteModal = () => {
    setPendingDeleteItem(null)
  }

  const handleConfirmDeleteModal = () => {
    const itemId = pendingDeleteItem?.id
    if (!itemId) return
    handleDelete(itemId)
    setPendingDeleteItem(null)
  }

  const handleToggleTaskVisibility = (itemId) => {
    setClosedTaskLists((prev) => toggleSetMember(prev, itemId, !prev.has(itemId)))
  }

  /* ── Task handlers ── */

  const saveGoalTasks = (item, updatedTasks) => {
    setTemporaryTasksByGoalId((prev) => ({ ...prev, [item.id]: updatedTasks }))
    setItems((prev) => {
      const updated = prev.map((currentItem) =>
        currentItem.id === item.id ? { ...currentItem, tasks: updatedTasks } : currentItem
      )
      saveItems(updated)
      return updated
    })
  }

  const handleToggleTask = async (item, taskId) => {
    if (isSampleGoalItem(item)) {
      const targetTask = getGoalTasks(item).find((task) => task.id === taskId)
      await toggleSampleGoalStepComplete(taskId, !targetTask?.completed)
      return
    }

    const updatedTasks = getGoalTasks(item).map((task) =>
      task.id === taskId ? { ...task, completed: !task.completed } : task
    )
    saveGoalTasks(item, updatedTasks)
  }

  const handleAddTask = (item) => {
    if (isSampleGoalItem(item)) return
    saveGoalTasks(item, [...getGoalTasks(item), { id: uuidv4(), name: '', completed: false }])
  }

  const handleEditTask = (item, taskId, newName) => {
    if (isSampleGoalItem(item) || !newName) return
    const updatedTasks = getGoalTasks(item).map((task) => (task.id === taskId ? { ...task, name: newName } : task))
    saveGoalTasks(item, updatedTasks)
  }

  const handleDeleteTask = (item, taskId) => {
    if (isSampleGoalItem(item)) return
    saveGoalTasks(item, getGoalTasks(item).filter((task) => task.id !== taskId))
  }

  /* ── Modal handlers ── */

  const addGoalCategoryOption = (nextCategory = '') => {
    const trimmed = nextCategory.trim()
    if (!trimmed) return

    setAddedGoalCategories((prev) => {
      const alreadyAdded = prev.some((category) => category.toLowerCase() === trimmed.toLowerCase())
      return alreadyAdded ? prev : [...prev, trimmed]
    })
  }

  const handleAddModalStep = () => {
    const newStepId = uuidv4()
    setModalSteps((prev) => [...prev, { id: newStepId, name: '' }])
    setFocusedStepId(newStepId)
  }

  const handleChangeModalStep = (stepId, name) => {
    setModalSteps((prev) => prev.map((step) => (step.id === stepId ? { ...step, name } : step)))
  }

  const handleRemoveModalStep = (stepId) => {
    setModalSteps((prev) => prev.filter((step) => step.id !== stepId))
  }

  const handleOpenAdd = () => {
    setEditingId(null)
    setModalValue('')
    setModalTargetDate('')
    setModalCategory('')
    setModalColor(DEFAULT_GOAL_COLOR)
    setCategorySearchQuery('')
    setModalLinked(true)
    setModalSteps([])
    setShowModal(true)
  }

  const handleOpenEdit = (item) => {
    setEditingId(item.id)
    setModalValue(item.name)
    setModalTargetDate(toInputDate(item.targetDate ?? ''))
    setModalCategory(item.category ?? '')
    setModalColor(CareerPlanColors[item.color] ? item.color : DEFAULT_GOAL_COLOR)
    setCategorySearchQuery('')
    setModalLinked(item.linked ?? true)
    setModalSteps(getModalStepsFromTasks(item.tasks))
    setShowModal(true)
  }

  const handleModalClose = () => {
    setShowModal(false)
    setModalValue('')
    setModalTargetDate('')
    setModalCategory('')
    setModalColor(DEFAULT_GOAL_COLOR)
    setCategorySearchQuery('')
    setModalLinked(true)
    setModalSteps([])
    setEditingId(null)
    setIsSaving(false)
    setFocusedStepId(null)
  }

  const handleModalSave = async () => {
    const trimmed = modalValue.trim()
    if (!trimmed || isSaving) return

    setIsSaving(true)
    try {
      if (editingId) {
        const updatedItems = items.map((item) => {
          if (item.id !== editingId) return item
          return {
            ...item,
            name: trimmed,
            ...(modalTargetDate ? { targetDate: new Date(`${modalTargetDate}T00:00:00`).toLocaleDateString() } : {}),
            ...(modalCategory.trim() ? { category: modalCategory.trim() } : {}),
            color: modalColor,
            linked: Boolean(modalLinked),
            tasks: buildTasksFromModal(modalSteps, item.tasks),
          }
        })
        setItems(updatedItems)
        await saveItems(updatedItems)
      } else {
        const newItem = {
          id: uuidv4(),
          name: trimmed,
          completed: false,
          linked: Boolean(modalLinked),
          date: new Date().toLocaleDateString(),
          ...(modalTargetDate ? { targetDate: new Date(`${modalTargetDate}T00:00:00`).toLocaleDateString() } : {}),
          ...(modalCategory.trim() ? { category: modalCategory.trim() } : {}),
          color: modalColor,
          tasks: buildTasksFromModal(modalSteps),
        }
        const updatedItems = [...items, newItem]
        setItems(updatedItems)
        await saveItems(updatedItems)

        try {
          await notifier.handleGoalSetting(profile)
        } catch (e) {
          console.error('Error sending goal notification:', e)
        }
      }

      handleModalClose()
    } finally {
      setIsSaving(false)
    }
  }

  /* ── Export ── */

  const handleExport = () => {
    const wb = XLSX.utils.book_new()
    const headers = [
      intl.formatMessage({ id: 'goal-tracking-export-col-name', defaultMessage: 'Name' }),
      intl.formatMessage({ id: 'goal-tracking-export-col-date', defaultMessage: 'Date Added' }),
      intl.formatMessage({ id: 'goal-tracking-export-col-target-date', defaultMessage: 'Target Date' }),
      intl.formatMessage({ id: 'goal-tracking-export-col-status', defaultMessage: 'Status' }),
      intl.formatMessage({ id: 'goal-tracking-export-col-subtasks', defaultMessage: 'Subtasks' }),
    ]
    const rows = displayItems.map((item) => {
      const itemTasks = normalizeGoalTasks(item.tasks)
      const subtasksSummary = itemTasks.length > 0 ? `${itemTasks.filter((t) => t.completed).length}/${itemTasks.length} completed` : ''
      return [
        item.name,
        item.date ?? '',
        item.targetDate ?? '',
        item.completed
          ? intl.formatMessage({ id: 'dashboard-filter-completed', defaultMessage: 'Completed' })
          : intl.formatMessage({ id: 'dashboard-filter-active', defaultMessage: 'Active' }),
        subtasksSummary,
      ]
    })
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows])
    XLSX.utils.book_append_sheet(wb, ws, 'Goals')
    XLSX.writeFile(wb, 'goals.xlsx')
  }

  /* ── Render ── */

  return (
    <section className="goal-tracking">
      {/* Page header */}
      <header className="goal-tracking-header w-mb-2">
        <WingsEyebrow as="p">
          <FormattedMessage id="goal-tracking-eyebrow" defaultMessage="TRACK YOUR" />
        </WingsEyebrow>
        <WingsHeading level={1}>
          <FormattedMessage id="goal-tracking-title" defaultMessage="Goals" />
        </WingsHeading>
        <WingsText className="w-mt-1 w-max-w-65ch w-mx-auto">
          <FormattedMessage
            id="goal-tracking-overview-para"
            defaultMessage="Set goals, track your progress, and celebrate what's ahead. Add steps within each goal to break it into manageable tasks."
          />
        </WingsText>
      </header>

      {/* Toolbar: top row (category/add + search), bottom row (status filters + export) */}
      <div className="goal-tracking-toolbar w-mb-1">
        <div className="goal-tracking-toolbar-top">
          <div className="goal-tracking-toolbar-left w-gap-1">
            <SemanticDropdown
              value={categoryFilter}
              selection
              icon="chevron down"
              options={categoryFilterOptions}
              onChange={(e, data) => setCategoryFilter(data.value)}
              className="ais-MenuSelect"
              upward={false}
            />

            <WingsButton variant="primary" size="mini" onClick={handleOpenAdd}>
              <FormattedMessage id="goal-tracking-add-new" defaultMessage="Add New" />
            </WingsButton>
          </div>

          {displayItems.length > 0 && (
            <WingsSearchBar
              className="goal-tracking-search"
              placeholder={intl.formatMessage({
                id: 'goal-tracking-search-placeholder',
                defaultMessage: 'Search goals and tasks',
              })}
              value={itemSearchQuery}
              onChange={setItemSearchQuery}
            />
          )}
        </div>

        <div className="goal-tracking-toolbar-bottom">
          {displayItems.length > 0 && (
            <div className="goal-tracking-filters" role="group">
              {[
                { value: 'active', labelId: 'dashboard-filter-active', defaultLabel: 'Active' },
                { value: 'done', labelId: 'dashboard-filter-completed', defaultLabel: 'Completed' },
                { value: 'all', labelId: 'dashboard-filter-all', defaultLabel: 'All' },
              ].map((tab, index) => (
                <React.Fragment key={tab.value}>
                  {index > 0 && (
                    <span className="goal-tracking-filter-sep" aria-hidden="true">
                      |
                    </span>
                  )}
                  <button
                    className={`goal-tracking-filter-button${filter === tab.value ? ' goal-tracking-filter-button-active' : ''}`}
                    onClick={() => {
                      setFilter(tab.value)
                      setCategoryFilter('all')
                    }}
                  >
                    <FormattedMessage id={tab.labelId} defaultMessage={tab.defaultLabel} />
                  </button>
                </React.Fragment>
              ))}
            </div>
          )}

          <button
            className="goal-tracking-export-button"
            onClick={handleExport}
            aria-label={intl.formatMessage({ id: 'goal-tracking-export', defaultMessage: 'Export' })}
          >
            <FontAwesomeIcon icon={faArrowToBottom} />
          </button>
        </div>
      </div>

      {/* Card grid or empty state */}
      {filteredItems.length > 0 ? (
        <div className="goal-tracking-grid">
          {filteredItems.map((item) => (
            <GoalCard
              key={item.id}
              item={item}
              tasks={getGoalTasks(item)}
              isTasksClosed={closedTaskLists.has(item.id)}
              isSampleGoal={isSampleGoalItem(item)}
              isBusy={isSampleGoalItem(item) && (sampleGoalLoading || sampleGoalSaving)}
              onToggleTaskVisibility={handleToggleTaskVisibility}
              onToggleTask={handleToggleTask}
              onEditTask={handleEditTask}
              onDeleteTask={handleDeleteTask}
              onAddTask={handleAddTask}
              onToggleComplete={handleToggleComplete}
              onEdit={handleOpenEdit}
              onDelete={handleOpenDeleteModal}
            />
          ))}
        </div>
      ) : (
        <div className="goal-tracking-empty">
          <blockquote className="component-blockquote component-blockquote-stories">
            <p>
              <FormattedMessage id="goal-tracking-empty-hint" defaultMessage='Get started by selecting "Add New"' />
            </p>
          </blockquote>
        </div>
      )}

      {/* Add / Edit modal */}
      <WingsModal
        isOpen={showModal}
        onClose={handleModalClose}
        size="md"
        className="goal-tracking-modal"
        title={
          editingId ? (
            <FormattedMessage id="goal-tracking-edit-title" defaultMessage="Edit Goal" />
          ) : (
            <FormattedMessage id="goal-tracking-add-title" defaultMessage="Add Goal" />
          )
        }
        titleLevel={5}
        headerAlign="left"
        primaryActionLabel={
          isSaving ? (
            <FormattedMessage id="goal-tracking-saving" defaultMessage="Saving..." />
          ) : (
            <FormattedMessage id="goal-tracking-save" defaultMessage="Save" />
          )
        }
        onPrimaryAction={handleModalSave}
        primaryActionDisabled={!modalValue.trim() || isSaving}
      >
        <WingsModal.Body>
          <WingsTextArea
            id="goal-description"
            placeholder={intl.formatMessage({
              id: 'goal-tracking-modal-placeholder',
              defaultMessage: 'Describe your goal...',
            })}
            value={modalValue}
            onChange={(e) => setModalValue(e.target.value)}
            rows={3}
            fullWidth
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey) && !isSaving) handleModalSave()
            }}
          />

          {/* Category: searchable dropdown with custom additions. */}
          <div className="goal-tracking-modal-date-row">
            <label className="goal-tracking-modal-date-label w-mb-1" htmlFor="goal-category-select">
              <FormattedMessage id="goal-tracking-modal-category-label" defaultMessage="Category" />
            </label>
            <WingsSelectInput
              id="goal-category-select"
              upward={false}
              fullWidth
              allowAdditions
              options={goalCategoryDropdownOptions}
              value={modalCategory}
              onSearchChange={(query) => setCategorySearchQuery(query)}
              onAddItem={(value) => {
                const nextCategory = (value ?? categorySearchQuery ?? '').toString().trim()
                if (!nextCategory) return
                setModalCategory(nextCategory)
                setCategorySearchQuery(nextCategory)
                addGoalCategoryOption(nextCategory)
              }}
              onChange={(value) => {
                const selectedValue = (value ?? '').toString().trim()
                const typedValue = (categorySearchQuery ?? '').toString().trim()
                const nextValue = selectedValue || typedValue
                setModalCategory(nextValue)
                if (nextValue) addGoalCategoryOption(nextValue)
              }}
              onBlur={() => {
                // Keep whatever the user typed even if they never pressed
                // Enter to formally "add" it.
                const typedValue = (categorySearchQuery ?? '').toString().trim()
                if (!typedValue) return
                setModalCategory((previousValue) => previousValue || typedValue)
                addGoalCategoryOption(typedValue)
              }}
              placeholder={intl.formatMessage({
                id: 'goal-tracking-modal-category-placeholder',
                defaultMessage: 'Career, School, Personal, etc.',
              })}
              className="goal-tracking-modal-category-select"
              noResultsMessage={intl.formatMessage({
                id: 'goal-tracking-modal-category-no-results',
                defaultMessage: 'No matches. Press Enter to add a new category.',
              })}
            />
          </div>

          <div className="goal-tracking-modal-date-row">
            <label className="goal-tracking-modal-date-label w-mb-1" htmlFor="goal-color-select">
              <FormattedMessage id="goal-tracking-modal-color-label" defaultMessage="Color" />
            </label>
            <WingsSelectInput
              id="goal-color-select"
              upward={false}
              fullWidth
              search={false}
              options={goalColorDropdownOptions}
              value={modalColor}
              onChange={(value) => setModalColor(value)}
            />
          </div>

          <WingsDateInput
            label={intl.formatMessage({ id: 'goal-tracking-modal-target-date-label', defaultMessage: 'Target Date' })}
            id="goal-target-date"
            value={modalTargetDate}
            onChange={(iso) => setModalTargetDate(iso)}
            fullWidth
          />

          <div className="goal-tracking-modal-date-row">
            <label className="goal-tracking-modal-date-label w-mb-1" htmlFor="goal-steps">
              <FormattedMessage id="goal-tracking-modal-steps-label" defaultMessage="Steps" />
            </label>
            <div id="goal-steps" className="goal-tracking-modal-steps-list">
              {modalSteps.map((step, index) => (
                <div key={step.id} className="goal-tracking-modal-step-row">
                  <WingsTextArea
                    className="goal-tracking-modal-step-input"
                    id={`goal-step-${step.id}`}
                    value={step.name}
                    onChange={(e) => handleChangeModalStep(step.id, e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault()
                        if (!isSaving) handleModalSave()
                      }
                    }}
                    placeholder={intl.formatMessage(
                      { id: 'goal-tracking-modal-step-placeholder', defaultMessage: 'Step {number}' },
                      { number: index + 1 }
                    )}
                    rows={1}
                    autoResize
                    fullWidth
                    autoFocus={step.id === focusedStepId}
                  />
                  <button
                    type="button"
                    className="goal-tracking-modal-step-remove"
                    onClick={() => handleRemoveModalStep(step.id)}
                    aria-label={intl.formatMessage({ id: 'goal-tracking-modal-step-remove', defaultMessage: 'Remove step' })}
                  >
                    <FontAwesomeIcon icon={faTimes} />
                  </button>
                </div>
              ))}

              <WingsButton variant="secondary" size="mini" onClick={handleAddModalStep} style={{ maxWidth: '100px' }}>
                <FormattedMessage id="goal-tracking-modal-step-add" defaultMessage="Add Step" />
              </WingsButton>
            </div>
          </div>

          <div className="goal-tracking-modal-checkbox-row w-mt-1">
            <input
              id="goal-linked-action-plan"
              type="checkbox"
              className="goal-tracking-modal-checkbox-input"
              checked={modalLinked}
              onChange={(e) => setModalLinked(e.target.checked)}
            />
            <label className="goal-tracking-modal-checkbox-label" htmlFor="goal-linked-action-plan">
              <FormattedMessage id="goal-tracking-modal-add-to-action-plan" defaultMessage="Add to action plan" />
            </label>
          </div>
        </WingsModal.Body>
      </WingsModal>

      <WingsModal
        isOpen={Boolean(pendingDeleteItem)}
        onClose={handleCloseDeleteModal}
        size="sm"
        className="goal-tracking-delete-modal"
        title={<FormattedMessage id="dashboard-item-menu-delete" defaultMessage="Delete" />}
        titleLevel={6}
        headerAlign="left"
        primaryActionLabel={<FormattedMessage id="dashboard-item-menu-delete" defaultMessage="Delete" />}
        onPrimaryAction={handleConfirmDeleteModal}
      >
        <WingsModal.Body>
          <WingsText>
            <FormattedMessage
              id="goal-tracking-delete-warning"
              defaultMessage="Are you sure you want to delete this goal?"
            />
          </WingsText>
        </WingsModal.Body>
      </WingsModal>
    </section>
  )
}

export default GoalTrackingTab